import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader, ArrowRight } from 'lucide-react';
import './PaymentSuccess.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    if (bookingId) {
      fetchBookingStatus(bookingId);
    } else {
      setError('No booking reference found');
      setStatus('error');
    }
  }, [bookingId]);

  const fetchBookingStatus = async (id) => {
    try {
      const response = await axios.get(`${API}/payments/order-status/${id}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentData(data);
        setStatus('success');
      } else {
        // Payment might still be processing, retry once
        setTimeout(async () => {
          try {
            const retry = await axios.get(`${API}/payments/order-status/${id}`);
            if (retry.data.payment_status === 'paid') {
              setPaymentData(retry.data);
              setStatus('success');
            } else {
              setPaymentData(retry.data);
              setStatus('pending');
            }
          } catch {
            setPaymentData(data);
            setStatus('pending');
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Error checking booking status:', err);
      setError('Could not retrieve booking details. Please check your email for confirmation.');
      setStatus('error');
    }
  };

  return (
    <div className="payment-success-page" data-testid="payment-success-page">
      <div className="container">
        <div className="payment-status-card">
          {status === 'loading' && (
            <>
              <div className="status-icon loading">
                <Loader size={64} className="spinner" />
              </div>
              <h1>Confirming Payment</h1>
              <p>Please wait while we confirm your booking...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="status-icon success">
                <CheckCircle size={64} />
              </div>
              <h1>Payment Successful!</h1>
              <p>Thank you for your booking. You will receive a confirmation email shortly.</p>
              {paymentData && (
                <div className="payment-details" data-testid="payment-details">
                  <div className="detail-row">
                    <span>Amount Paid:</span>
                    <span>${paymentData.amount_total?.toFixed(2)} {paymentData.currency?.toUpperCase()}</span>
                  </div>
                  {paymentData.package_name && (
                    <div className="detail-row">
                      <span>Package:</span>
                      <span>{paymentData.package_name}</span>
                    </div>
                  )}
                  {paymentData.booking_id && (
                    <div className="detail-row">
                      <span>Booking Reference:</span>
                      <span>{paymentData.booking_id.slice(0, 8).toUpperCase()}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="action-buttons">
                <Link to="/" className="btn-primary" data-testid="return-home-btn">
                  Return Home <ArrowRight size={18} />
                </Link>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className="status-icon success">
                <CheckCircle size={64} />
              </div>
              <h1>Booking Received!</h1>
              <p>Your booking has been submitted and payment is being processed. You will receive a confirmation email shortly.</p>
              {paymentData && paymentData.booking_id && (
                <div className="payment-details">
                  <div className="detail-row">
                    <span>Booking Reference:</span>
                    <span>{paymentData.booking_id.slice(0, 8).toUpperCase()}</span>
                  </div>
                </div>
              )}
              <div className="action-buttons">
                <Link to="/" className="btn-primary" data-testid="return-home-btn">
                  Return Home <ArrowRight size={18} />
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="status-icon error">
                <XCircle size={64} />
              </div>
              <h1>Something Went Wrong</h1>
              <p>{error}</p>
              <div className="action-buttons">
                <Link to="/packages" className="btn-primary" data-testid="view-packages-btn">
                  View Packages <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="btn-secondary" data-testid="contact-support-btn">
                  Contact Support
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
